import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { JobOffersList } from "@/components/jobs/JobOffersList";
import { useJobsPaginated } from "@/hooks/jobs/useJobsPaginated";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { useJobsFilter } from "@/contexts/JobsFilterContext";
import { useAuth } from "@/providers/AuthProvider";
import { JobData } from "@/hooks/jobs/useJobsData";

interface JobsContentProps {
  jobs?: JobData[];
  isLoading?: boolean;
  viewMode: "card" | "list";
  onRefresh?: () => void;
  refreshInterval?: number;
  enableAutoRefresh?: boolean;
  onDeleteJobs?: (jobIds: string[]) => Promise<void>;
}

export const JobsContent = ({ 
  viewMode, 
  onRefresh,
  refreshInterval = 30000,
  enableAutoRefresh = false,
  onDeleteJobs
}: JobsContentProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const {
    searchTerm,
    skillsFilter,
    skillsOperator,
    statusFilter,
    showClosed,
    maxDaysAgo,
    assignedToMe,
    showFavoritesOnly,
    sortField,
    sortOrder
  } = useJobsFilter();
  
  const { favorites } = useUserFavorites('job');
  
  // Use new paginated hook with database-level filtering
  const {
    jobs: paginatedJobs,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    refetch
  } = useJobsPaginated(
    {
      searchTerm,
      skillsFilter,
      skillsOperator,
      statusFilter,
      showClosed,
      maxDaysAgo,
      assignedToMe,
      currentUserId: user?.id || null,
      showFavoritesOnly,
      favorites
    },
    sortField,
    sortOrder
  );
  
  // Set up automatic refresh interval only when explicitly enabled
  useEffect(() => {
    if (!enableAutoRefresh) return;
    
    // Set up interval for periodic refreshes
    const interval = setInterval(() => {
      refetch();
      onRefresh?.();
    }, refreshInterval);
    
    // Clean up on unmount
    return () => clearInterval(interval);
  }, [refetch, onRefresh, refreshInterval, enableAutoRefresh]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <p>{t("jobs.loading", "Loading job offers...")}</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <JobOffersList 
        jobs={paginatedJobs} 
        viewMode={viewMode}
        onDeleteJobs={onDeleteJobs}
        onJobsChange={() => {
          refetch();
          onRefresh?.();
        }}
        favorites={favorites}
      />
    </div>
  );
};
